import { ComboboxItem, OptionsFilter, defaultOptionsFilter, isOptionsGroup } from '@mantine/core';
import { useCallback, useMemo } from 'react';

import { useGetPages, isFacebookMessengerPage, InstPage, TgPage } from '@/features/pages';
import { Departments, Platforms } from '@/types';

type UsePagesOptionsParams = {
  /** `undefined` and `'all'` both mean "every platform". */
  platform?: Platforms | 'all';
  department?: Departments;
  /** Page ids rendered as non-selectable options. */
  disabled?: string[];
};

const formatPages = (pages: (TgPage | InstPage)[], disabled?: string[]): ComboboxItem[] =>
  pages.map((page) => ({
    label: page.name + (page.username ? ` (${page.username})` : ''),
    value: page._id,
    disabled: disabled?.includes(page._id),
  }));

/**
 * Mantine keeps a group in the dropdown even when search or `hidePickedOptions`
 * removed every one of its options, which leaves a dangling group header behind.
 *
 * This is the only place empty groups may be dropped: `MultiSelect` derives
 * `hiddenWhenEmpty` from the number of groups in `data` before this filter runs,
 * so pruning them earlier hides the dropdown along with `nothingFoundMessage`.
 */
export const nonEmptyGroupsFilter: OptionsFilter = (input) =>
  defaultOptionsFilter(input).filter((item) => !isOptionsGroup(item) || item.items.length > 0);

/**
 * Page options grouped by channel — Telegram, Instagram and Facebook Messenger.
 * Instagram and Facebook Messenger share the same platform on the server, so they are
 * told apart by `integration` and shown under separate headers (DEV-2224).
 */
export const usePagesOptions = ({ platform, department, disabled }: UsePagesOptionsParams) => {
  const isPlatformEnabled = useCallback(
    (p: Platforms) => !platform || platform === 'all' || platform === p,
    [platform]
  );

  const getTgPages = useGetPages({
    platform: Platforms.telegram,
    department,
    config: { enabled: isPlatformEnabled(Platforms.telegram) },
  });
  const getMetaPages = useGetPages({
    platform: Platforms.instagram,
    department,
    config: { enabled: isPlatformEnabled(Platforms.instagram) },
  });

  const { options, pagesCount } = useMemo(() => {
    const tgPages = (isPlatformEnabled(Platforms.telegram) && getTgPages.data?.pages) || [];
    const metaPages = (isPlatformEnabled(Platforms.instagram) && getMetaPages.data?.pages) || [];

    return {
      options: [
        { group: 'Telegram', items: formatPages(tgPages, disabled) },
        {
          group: 'Instagram',
          items: formatPages(
            metaPages.filter((page) => !isFacebookMessengerPage(page)),
            disabled
          ),
        },
        {
          group: 'Facebook Messenger',
          items: formatPages(metaPages.filter(isFacebookMessengerPage), disabled),
        },
      ],
      pagesCount: tgPages.length + metaPages.length,
    };
  }, [getTgPages.data, getMetaPages.data, disabled, isPlatformEnabled]);

  return { options, pagesCount, isFetching: getTgPages.isFetching || getMetaPages.isFetching };
};
